import { Link } from "react-router-dom";
import { Download, ArrowRight, Waves, Building2, Trophy, Plane, ShoppingBag, Shield, CheckCircle2, Factory, MapPin, Award, TrendingUp } from "lucide-react";

/**
 * Living Waters US — Distribution Partnership Landing Page (public marketing surface)
 * URL: /living-waters
 *
 * Positions Sterile Mate LLC DBA Bleu Flux as the primary distribution engine
 * for Living Waters US "Hydration for Every Moment". Emphasizes the STRENGTH of
 * Sterile Mate LLC (government contracts, healthcare vendor status, HUB certification).
 *
 * All hero + section imagery generated via Nano Banana and served from
 * /api/static/email_assets/lw_*.jpg
 */
export default function LivingWatersLandingPage() {
  const publicBase = (process.env.REACT_APP_PUBLIC_URL || "https://sterilemate.com").replace(/\/$/, "");
  const backendBase = (process.env.REACT_APP_BACKEND_URL || publicBase).replace(/\/$/, "");
  const pdfUrl = `${backendBase}/api/proposals/living-waters/pdf`;
  const proposalUrl = "/proposals/living-waters";
  const img = (name) => `${backendBase}/api/static/email_assets/${name}`;

  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-x-hidden" data-testid="living-waters-landing-page">

      {/* ============ HERO ============ */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={img("lw_bottle_lineup.jpg")}
            alt="Living Waters US premium 16.9 oz bottled water lineup"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-950/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/15 border border-amber-400/30 text-amber-300 text-xs font-bold tracking-[0.25em] uppercase">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
              Proposal · Exclusive Regional Sales &amp; Distribution
            </div>
            <h1 className="mt-6 text-5xl md:text-6xl lg:text-7xl font-black leading-[1.05]">
              A partnership proposal<br/>
              <span className="bg-gradient-to-r from-amber-400 via-amber-300 to-orange-400 bg-clip-text text-transparent">
                for Living Waters US.
              </span>
            </h1>
            <p className="mt-6 text-xl md:text-2xl text-blue-100 leading-snug font-light">
              <em>Hydration for Every Moment</em> &mdash; ready to be poured everywhere hydration matters.
            </p>
            <p className="mt-6 text-base md:text-lg text-blue-100/90 max-w-2xl leading-relaxed">
              <span className="font-bold text-white">Sterile Mate LLC, doing business as Bleu Flux</span>, is respectfully proposing to serve as your <span className="font-bold text-white">primary and exclusive Sales &amp; Distribution partner</span> across <span className="text-amber-300 font-semibold">Texas and the Gulf Coast region (LA · AL · MS)</span> to start, with national expansion earned as we prove the model. We are a Texas-headquartered, HUB-certified healthcare and government vendor with active contracts across the City of Houston, Harris County, and State of Texas &mdash; including <span className="text-amber-300 font-semibold">ten state-level water purification contracts and five county bids in the seven figures</span>.
            </p>
            <p className="mt-4 text-lg text-cyan-200 font-semibold">
              We&apos;re not asking you to hire a startup distributor. We&apos;re offering you <span className="text-white">the strength of Sterile Mate LLC</span> &mdash; a vendor stack already turned on.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href={pdfUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="lw-hero-download-pdf"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold shadow-lg shadow-amber-500/20 transition-all hover:shadow-2xl hover:-translate-y-0.5"
              >
                <Download className="w-5 h-5" />
                Download the Proposal (PDF)
              </a>
              <Link
                to={proposalUrl}
                data-testid="lw-hero-view-proposal"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-white/10 backdrop-blur border border-white/20 hover:bg-white/15 text-white font-semibold transition-all"
              >
                Read the Full Proposal
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>

            {/* At-a-glance strip */}
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { n: "5 yrs", l: "Exclusive term" },
                { n: "15%", l: "Commission of wholesale" },
                { n: "14/day", l: "Pallets · 24,192 bottles" },
                { n: "$0.057", l: "per bottle to Bleu Flux" },
              ].map((s) => (
                <div key={s.l} className="bg-white/5 border border-white/10 backdrop-blur rounded-xl p-4">
                  <p className="text-2xl md:text-3xl font-black text-amber-400">{s.n}</p>
                  <p className="text-[10px] uppercase tracking-widest text-blue-200 mt-1 leading-tight">{s.l}</p>
                </div>
              ))}
            </div>
            <p className="mt-3 text-[10px] text-blue-300/70">15% commission calculated on $0.38 wholesale price per bottle. See proforma below.</p>
          </div>
        </div>
      </section>

      {/* ============ STRENGTH OF STERILE MATE ============ */}
      <section className="relative py-24 px-6 lg:px-10 bg-gradient-to-b from-slate-950 via-blue-950 to-slate-950">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <p className="text-xs font-bold tracking-[0.3em] text-cyan-400 uppercase">Why Bleu Flux &mdash; The Strength of Sterile Mate LLC</p>
            <h2 className="mt-3 text-4xl md:text-5xl font-black text-white leading-tight">
              You would not be hiring a startup distributor.<br/>
              <span className="text-amber-400">You would be inheriting an active government vendor stack.</span>
            </h2>
            <p className="mt-5 text-lg text-blue-100 leading-relaxed">
              Sterile Mate LLC is a six-year-old, HUB-certified Texas healthcare company operating today as a credentialed vendor to the largest municipal and state buyers in Texas. Those vendor relationships took years to earn &mdash; and every one of them consumes bottled water on a repeating institutional basis. If you choose Bleu Flux, Living Waters would plug straight into infrastructure that is <span className="text-amber-300 font-semibold">already turned on</span>.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Building2, color: "text-blue-300", stat: "3 tiers", title: "City of Houston · Harris County · State of Texas", body: "Master vendor files current. HUB certification active. Insurance and bonding in place. Registered as both Medical Device Supplier and Water Purification Infrastructure Partner." },
              { icon: Trophy, color: "text-amber-400", stat: "5 wins", title: "County Bids — 7-Figure Values", body: "Five Texas county contract awards with individual values in the seven figures. Established Buy Board, TIPS, BuyBoard-Coop, and direct county purchasing pathways." },
              { icon: Waves, color: "text-cyan-300", stat: "10 active", title: "State Water Purification Contracts", body: "Ten currently active state-level contracts for water purification and treatment infrastructure. The same buyer's desk reorders bottled water — and already knows Sterile Mate." },
              { icon: Shield, color: "text-emerald-400", stat: "6 yrs", title: "Credentialed Healthcare Vendor", body: "Harris Health, Memorial Hermann + additional systems. Cafeterias, patient-experience programs, employee wellness, and community-health events all consume bottled water — usually from three different distributors. We consolidate to one." },
              { icon: Plane, color: "text-indigo-300", stat: "IAH · HOU · EFD", title: "Airport Concession Pathway", body: "Direct pathway into Houston Airport System concession programs, anchored by City of Houston vendor status and HUB certification. Nationally scalable through DBE joint ventures at DFW, ATL, LAX." },
              { icon: Award, color: "text-fuchsia-300", stat: "HUB", title: "Certified Minority Vendor", body: "Historically Underutilized Business certification unlocks minority-set-aside line items across every government contract vehicle. Living Waters instantly inherits eligibility for procurement channels closed to non-certified competitors." },
            ].map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.title} className="group bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 rounded-2xl p-6 transition-all hover:-translate-y-1 hover:border-amber-400/40">
                  <div className="flex items-start justify-between">
                    <Icon className={`w-9 h-9 ${c.color}`} />
                    <span className="text-[10px] font-mono text-amber-300/80 bg-amber-500/10 border border-amber-400/20 px-2 py-1 rounded-full">{c.stat}</span>
                  </div>
                  <p className="mt-4 font-bold text-white text-base leading-tight">{c.title}</p>
                  <p className="mt-2 text-sm text-blue-100/80 leading-6">{c.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ FOUR CHANNELS ============ */}
      <section className="relative py-24 px-6 lg:px-10 bg-white text-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <p className="text-xs font-bold tracking-[0.3em] text-cyan-700 uppercase">Four Channels · One Exclusive Partner</p>
              <h2 className="mt-3 text-4xl md:text-5xl font-black text-blue-950 leading-tight">
                Where Living Waters ends up on the shelf, in the crate, or in the cooler.
              </h2>
              <p className="mt-6 text-lg text-slate-700 leading-relaxed">
                Bleu Flux — powered by Sterile Mate LLC — activates all four distribution channels under a single exclusive agreement. One partner. One PO number. One monthly scorecard.
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { icon: Building2, tint: "from-blue-600 to-blue-800", title: "Government &amp; Municipal", body: "City of Houston · Harris County · State of Texas contract vehicles (TxSmartBuy, DIR, TIPS, BuyBoard, TXMAS)." },
                  { icon: ShoppingBag, tint: "from-emerald-600 to-emerald-800", title: "Retail &amp; Convenience", body: "H-E-B · Kroger regional buyers · independent grocery in Houston, DFW, Austin, San Antonio. DSD convenience routes." },
                  { icon: Trophy, tint: "from-amber-500 to-orange-600", title: "Tradeshows &amp; Sponsorships", body: "Healthcare conferences (AAMI, APIC, HIDA, AONL), municipal-safety expos (TML, TAC), veteran & community events, B2B trade expos." },
                  { icon: Plane, tint: "from-indigo-600 to-fuchsia-700", title: "Airport Concessions", body: "IAH · HOU · EFD terminal retail via Hudson / Paradies-Lagardère. Airline crew-provisioning. Scalable to DFW, ATL, LAX." },
                ].map((c) => {
                  const Icon = c.icon;
                  return (
                    <div key={c.title} className="group rounded-2xl overflow-hidden border border-slate-200 hover:shadow-2xl transition-all hover:-translate-y-1">
                      <div className={`bg-gradient-to-br ${c.tint} p-5`}>
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <div className="p-5">
                        <p className="font-bold text-blue-950 text-base" dangerouslySetInnerHTML={{ __html: c.title }} />
                        <p className="mt-2 text-sm text-slate-600 leading-6">{c.body}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ VISUAL: WHERE IT SHOWS UP ============ */}
      <section className="relative py-24 px-6 lg:px-10 bg-slate-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <p className="text-xs font-bold tracking-[0.3em] text-cyan-700 uppercase">Every Moment. Every Bottle. Across Texas &amp; the Gulf Coast.</p>
            <h2 className="mt-3 text-4xl md:text-5xl font-black text-blue-950 leading-tight">
              Where <span className="text-amber-500">Hydration for Every Moment</span> lives &mdash; starting locally.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { src: "lw_lifestyle_athlete.jpg", label: "First Responders &amp; Athletes", sub: "5K races · veterans events · fire &amp; EMS staging" },
              { src: "lw_hospital_courtyard.jpg", label: "Hospitals &amp; Patient Families", sub: "Cafeterias · wellness rooms · community health" },
              { src: "lw_airport_traveler.jpg", label: "Airport Travelers &amp; Crew", sub: "Terminal retail · lounges · airline provisioning" },
              { src: "lw_texas_capitol.jpg", label: "Government &amp; Municipal", sub: "State agencies · TxDOT · Capitol events" },
            ].map((it) => (
              <div key={it.src} className="relative group rounded-2xl overflow-hidden bg-slate-900 aspect-[4/5]">
                <img
                  src={img(it.src)}
                  alt={it.label.replace(/&amp;/g, "&")}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <p className="font-bold text-white text-lg leading-tight" dangerouslySetInnerHTML={{ __html: it.label }} />
                  <p className="mt-1 text-xs text-blue-100 leading-5" dangerouslySetInnerHTML={{ __html: it.sub }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ COMMERCIAL FRAMEWORK ============ */}
      <section className="relative py-24 px-6 lg:px-10 bg-gradient-to-b from-blue-950 via-slate-950 to-slate-950 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <p className="text-xs font-bold tracking-[0.3em] text-amber-400 uppercase">Commercial Framework</p>
            <h2 className="mt-3 text-4xl md:text-5xl font-black leading-tight">
              15% of the wholesale price.<br/>
              <span className="text-amber-400">You keep 85% of every sale.</span>
            </h2>
            <p className="mt-5 text-lg text-blue-100/90 leading-relaxed">
              Bleu Flux is paid a straight <b className="text-white">15% commission on your wholesale price of $0.38/bottle</b> &mdash; that&apos;s <b className="text-amber-300">$0.057 per bottle</b> to us. Living Waters keeps <b className="text-white">$0.323 per bottle</b> ($7.75/case, $558.14/pallet) before your own production cost.
            </p>
          </div>

          <div className="mt-12 grid lg:grid-cols-2 gap-6">
            <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-6">
              <h3 className="text-sm font-bold text-amber-400 uppercase tracking-widest mb-4">Deal Terms</h3>
              <table className="w-full text-sm">
                <tbody className="divide-y divide-white/10">
                  {[
                    ["Term", "5 years (1-year initial + 4-year auto-renewal)"],
                    ["Territory", "United States (Exclusive)"],
                    ["Product", "16.9 oz BPA-free PET bottles"],
                    ["Pack Configuration", "24 bottles / case · 72 cases / pallet · 1,728 bottles / pallet"],
                    ["Wholesale price / bottle", "$0.38"],
                    ["Commission (Bleu Flux)", "15% of wholesale = $0.057 per bottle", "amber"],
                    ["Per case commission", "$1.368 (24 × $0.057)"],
                    ["Per pallet commission", "$98.50 (1,728 × $0.057)"],
                    ["Production Commitment", "14 pallets / day × 5 days / week"],
                  ].map(([k, v, cls]) => (
                    <tr key={k}>
                      <td className="py-3 text-blue-200/70">{k}</td>
                      <td className={`py-3 text-right font-semibold ${cls === "amber" ? "text-amber-400" : "text-white"}`}>{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="rounded-2xl bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-transparent border border-amber-400/30 p-6">
              <h3 className="text-sm font-bold text-amber-400 uppercase tracking-widest mb-4">Bleu Flux Commission at 14 Pallets/Day × 5 Days/Week</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between items-baseline pb-2 border-b border-white/10"><span className="text-blue-200/80">14 pallets × 1,728 bottles</span><span className="font-mono font-bold text-white">24,192 bottles / day</span></div>
                <div className="flex justify-between items-baseline pb-2 border-b border-white/10"><span className="text-blue-200/80">24,192 × $0.057</span><span className="font-mono font-bold text-amber-400">$1,378.94 / day</span></div>
                <div className="flex justify-between items-baseline pb-2 border-b border-white/10"><span className="text-blue-200/80">× 5 days / week</span><span className="font-mono font-bold text-amber-400">$6,894.72 / week</span></div>
                <div className="flex justify-between items-baseline pb-2 border-b border-white/10"><span className="text-blue-200/80">× 52 weeks / year</span><span className="font-mono font-bold text-white">$358,525 / year</span></div>
                <div className="flex justify-between items-baseline pt-2"><span className="font-bold text-amber-400">Bleu Flux commission @ full run</span><span className="font-mono font-bold text-amber-400 text-xl">~$358K/yr</span></div>
              </div>
              <p className="mt-4 text-xs text-blue-200/70 leading-5">
                Ceiling scenario at 100% absorption of production capacity (6.29M bottles/yr). Bleu Flux commission is calculated on the <b className="text-white">$0.38 wholesale price</b>; Living Waters keeps the rest to cover production cost and margin.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ MARKETING EXPENSE REIMBURSEMENT ============ */}
      <section className="relative py-24 px-6 lg:px-10 bg-slate-100 text-slate-900" data-testid="lw-expense-allowance">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <p className="text-xs font-bold tracking-[0.3em] text-amber-700 uppercase">Marketing Expense Reimbursement</p>
              <h2 className="mt-3 text-4xl md:text-5xl font-black text-blue-950 leading-tight">
                No salary. <span className="text-amber-500">No retainer.</span><br/>
                Just what we actually spend.
              </h2>
              <p className="mt-5 text-lg text-slate-700 leading-relaxed">
                Bleu Flux is asking for one simple thing beyond commission: a straightforward <span className="font-bold text-blue-950">monthly reimbursement of documented marketing expenses</span> incurred while promoting Living Waters to our customers.
              </p>
              <p className="mt-4 text-base text-slate-600 leading-relaxed">
                We submit itemized receipts every month. You review. You reimburse only what was actually spent. No blank check. No monthly floor. No obligation to keep me on payroll &mdash; because I am not on payroll. I remain fully independent, running Sterile Mate on my own.
              </p>
            </div>

            <div className="lg:col-span-7">
              <div className="rounded-2xl bg-gradient-to-br from-blue-950 via-slate-900 to-cyan-950 text-white p-6 md:p-8 shadow-2xl">
                <p className="text-xs font-bold tracking-widest text-amber-400 uppercase">How It Works</p>
                <ol className="mt-4 space-y-4 text-sm">
                  {[
                    { n: "1", h: "Bleu Flux spends first.", b: "Every marketing dollar spent to promote Living Waters to our customers is documented at the moment of purchase (invoice + receipt captured to our ledger)." },
                    { n: "2", h: "Monthly reimbursement request.", b: "On the 1st of each month, Bleu Flux submits an itemized expense report for the prior month with receipts attached. Nothing else." },
                    { n: "3", h: "Living Waters reviews and reimburses.", b: "Living Waters reviews line-by-line, approves what fits the mutual scope, and reimburses only the approved amount within 15 days." },
                    { n: "4", h: "Zero spend, zero reimbursement.", b: "If Bleu Flux does not spend on marketing in a given month, Living Waters owes nothing. Reimbursement follows actual, documented outlay — never a floor." },
                  ].map((s) => (
                    <li key={s.n} className="flex items-start gap-3">
                      <span className="w-7 h-7 rounded-full bg-amber-400 text-slate-950 font-black flex items-center justify-center flex-shrink-0 text-sm">{s.n}</span>
                      <div>
                        <p className="font-bold text-white">{s.h}</p>
                        <p className="text-blue-100/80 text-xs mt-0.5 leading-5">{s.b}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Example categories */}
              <div className="mt-4 rounded-2xl bg-white border border-slate-200 p-6">
                <p className="text-xs font-bold tracking-widest text-cyan-700 uppercase">Examples of Reimbursable Marketing Spend</p>
                <p className="mt-2 text-xs text-slate-500">These are illustrative categories &mdash; not commitments. Bleu Flux only submits what was actually spent on Living Waters&apos; behalf.</p>
                <ul className="mt-3 grid sm:grid-cols-2 gap-2 text-sm text-slate-700">
                  {[
                    "Co-branded RFP collateral and buyer-meeting decks",
                    "Print of Living Waters flyers, spec sheets, one-pagers",
                    "Targeted LinkedIn / trade-press placements",
                    "Trade show booth fees and pallet drop-fees at TX / Gulf Coast events",
                    "Branded coolers, koozies, and event provisioning",
                    "Sample cases for buyer meetings (billed at cost, no markup)",
                    "Travel to in-region buyer meetings when required",
                  ].map((it) => (
                    <li key={it} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5"/>
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-xs text-slate-500 border-t border-slate-200 pt-3 leading-5">
                  <b className="text-slate-900">Guardrails Living Waters may want to add:</b> a mutually agreed monthly cap (e.g., not to exceed a threshold without written pre-approval), and quarterly reconciliation. Bleu Flux is comfortable with any reasonable structure Living Waters counsel proposes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ LIVING WATERS 3-YEAR PROFORMA ============ */}
      <section className="relative py-24 px-6 lg:px-10 bg-white text-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <p className="text-xs font-bold tracking-[0.3em] text-cyan-700 uppercase">Living Waters US · 3-Year Proforma</p>
            <h2 className="mt-3 text-4xl md:text-5xl font-black text-blue-950 leading-tight">
              What this deal <span className="text-amber-500">actually returns to Living Waters</span>.
            </h2>
            <p className="mt-5 text-lg text-slate-700 leading-relaxed">
              A candid 3-year revenue map for Living Waters ownership. Assumes production runs at 14 pallets/day, 5 days/week at the current <b>$0.38/bottle wholesale price</b>, with a realistic absorption ramp as Bleu Flux activates the four channels. This is <b>net-to-Living-Waters revenue</b> after Bleu Flux&apos;s 15% commission — before Living Waters&apos; own production cost.
            </p>
          </div>

          {/* Per-run micro table */}
          <div className="mt-10 grid md:grid-cols-3 gap-4">
            <div className="rounded-xl bg-blue-950 text-white p-6">
              <p className="text-xs font-bold tracking-widest text-amber-400 uppercase">Per Bottle</p>
              <div className="mt-3 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-blue-200">Wholesale price</span><span className="font-mono font-bold">$0.380</span></div>
                <div className="flex justify-between"><span className="text-blue-200">Bleu Flux 15% commission</span><span className="font-mono">$0.057</span></div>
                <div className="flex justify-between pt-2 border-t border-white/10 mt-2"><span className="font-bold">LW net revenue</span><span className="font-mono font-bold text-amber-400">$0.323</span></div>
              </div>
            </div>
            <div className="rounded-xl bg-blue-950 text-white p-6">
              <p className="text-xs font-bold tracking-widest text-amber-400 uppercase">Per Case (24 bottles)</p>
              <div className="mt-3 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-blue-200">Wholesale</span><span className="font-mono font-bold">$9.12</span></div>
                <div className="flex justify-between"><span className="text-blue-200">Commission</span><span className="font-mono">$1.37</span></div>
                <div className="flex justify-between pt-2 border-t border-white/10 mt-2"><span className="font-bold">LW net revenue</span><span className="font-mono font-bold text-amber-400">$7.75</span></div>
              </div>
            </div>
            <div className="rounded-xl bg-blue-950 text-white p-6">
              <p className="text-xs font-bold tracking-widest text-amber-400 uppercase">Per Pallet (1,728 bottles)</p>
              <div className="mt-3 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-blue-200">Wholesale</span><span className="font-mono font-bold">$656.64</span></div>
                <div className="flex justify-between"><span className="text-blue-200">Commission</span><span className="font-mono">$98.50</span></div>
                <div className="flex justify-between pt-2 border-t border-white/10 mt-2"><span className="font-bold">LW net revenue</span><span className="font-mono font-bold text-amber-400">$558.14</span></div>
              </div>
            </div>
          </div>

          {/* 3-year table */}
          <div className="mt-8 rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 p-6 md:p-8">
            <h3 className="text-sm font-bold text-cyan-700 uppercase tracking-widest mb-4">3-Year Revenue Map — Living Waters US Ownership</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b-2 border-slate-300">
                    <th className="text-left py-3 font-semibold">Year</th>
                    <th className="text-right py-3 font-semibold">Absorption</th>
                    <th className="text-right py-3 font-semibold">Annual Bottles</th>
                    <th className="text-right py-3 font-semibold">LW Wholesale Revenue</th>
                    <th className="text-right py-3 font-semibold">Bleu Flux 15%</th>
                    <th className="text-right py-3 font-semibold">Net to Living Waters</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {[
                    ["Year 1", 0.35, "Bleu Flux activation · TX vendor files + initial RFP wins"],
                    ["Year 2", 0.60, "Retail + airport concession openings · multi-state expansion"],
                    ["Year 3", 0.80, "Full national channel penetration · renewal window"],
                  ].map(([label, pct, note]) => {
                    const bottles = Math.round(24192 * 5 * 52 * pct);
                    const rev = bottles * 0.38;
                    const comm = bottles * 0.057;
                    const net = rev - comm;
                    return (
                      <tr key={label} className="hover:bg-white/60 transition-colors">
                        <td className="py-3">
                          <p className="font-bold text-blue-950">{label}</p>
                          <p className="text-[11px] text-slate-500 mt-0.5">{note}</p>
                        </td>
                        <td className="py-3 text-right text-slate-700">{Math.round(pct * 100)}%</td>
                        <td className="py-3 text-right font-mono text-slate-800">{bottles.toLocaleString()}</td>
                        <td className="py-3 text-right font-mono text-slate-900">${Math.round(rev).toLocaleString()}</td>
                        <td className="py-3 text-right font-mono text-slate-700">${Math.round(comm).toLocaleString()}</td>
                        <td className="py-3 text-right font-mono text-emerald-700 font-bold text-base">${Math.round(net).toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="border-t-2 border-blue-950 text-blue-950 font-bold">
                  <tr>
                    <td className="pt-4">3-Year Total to Living Waters</td>
                    <td className="pt-4 text-right">—</td>
                    <td className="pt-4 text-right font-mono">11,007,360</td>
                    <td className="pt-4 text-right font-mono">$4,182,797</td>
                    <td className="pt-4 text-right font-mono">$627,419</td>
                    <td className="pt-4 text-right font-mono text-emerald-700 text-lg">$3,555,378</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Big number */}
            <div className="mt-8 grid md:grid-cols-3 gap-4">
              <div className="rounded-xl bg-emerald-600 text-white p-6 text-center">
                <p className="text-xs font-bold tracking-widest text-emerald-100 uppercase">3-Year Net to LW</p>
                <p className="mt-2 text-4xl font-black">$3.56M</p>
                <p className="text-xs text-emerald-100 mt-1">after Bleu Flux commission, before LW COGS</p>
              </div>
              <div className="rounded-xl bg-slate-900 text-white p-6 text-center">
                <p className="text-xs font-bold tracking-widest text-slate-400 uppercase">3-Year Wholesale Revenue</p>
                <p className="mt-2 text-4xl font-black">$4.18M</p>
                <p className="text-xs text-slate-400 mt-1">at $0.38/bottle wholesale</p>
              </div>
              <div className="rounded-xl bg-amber-500 text-slate-950 p-6 text-center">
                <p className="text-xs font-bold tracking-widest text-slate-800 uppercase">3-Year Commission</p>
                <p className="mt-2 text-4xl font-black">$627K</p>
                <p className="text-xs text-slate-800 mt-1">total to Bleu Flux</p>
              </div>
            </div>

            <p className="mt-6 text-xs text-slate-500 leading-6 border-t border-slate-300 pt-4">
              <b>Assumptions:</b> $0.38/bottle wholesale price (client-provided); 14 pallets/day × 5 days/week × 52 weeks/year = 6,289,920 bottles annual capacity; ramp reflects realistic channel-onboarding pace. <b>Net-to-Living-Waters</b> = wholesale revenue − Bleu Flux 15% commission. <b>Excludes:</b> Living Waters&apos; production cost (COGS), packaging, labels, freight-to-warehouse, and capex — apply your own COGS to derive gross margin. This is a top-line revenue map, not a full P&amp;L.
            </p>
          </div>
        </div>
      </section>

      {/* ============ DELIVERABLES ============ */}
      <section className="relative py-24 px-6 lg:px-10 bg-white text-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <p className="text-xs font-bold tracking-[0.3em] text-cyan-700 uppercase">What Bleu Flux Delivers</p>
              <h2 className="mt-3 text-4xl md:text-5xl font-black text-blue-950 leading-tight">
                Ten commitments. In writing. Every quarter.
              </h2>
              <p className="mt-5 text-lg text-slate-700 leading-relaxed">
                This is the operating cadence Sterile Mate runs today for its healthcare customers — brought straight to Living Waters as your exclusive distribution partner.
              </p>
              <div className="mt-8 rounded-2xl bg-gradient-to-br from-blue-950 to-slate-900 text-white p-6 border border-blue-800">
                <p className="text-xs font-bold text-amber-400 uppercase tracking-widest">Monthly Scoreboard</p>
                <p className="mt-2 font-bold text-lg text-white">Transparent. Auditable. Aligned.</p>
                <ul className="mt-4 space-y-2 text-sm text-blue-100">
                  <li className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-400"/> Pipeline created + orders won</li>
                  <li className="flex items-center gap-2"><Factory className="w-4 h-4 text-emerald-400"/> Cases shipped + capacity absorbed</li>
                  <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-emerald-400"/> Channel mix (Gov / Retail / Events / Airport)</li>
                  <li className="flex items-center gap-2"><Award className="w-4 h-4 text-emerald-400"/> Commission earned + renewal target status</li>
                </ul>
              </div>
            </div>

            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
              {[
                "Vendor-of-record activation across City of Houston, Harris County, and State of Texas",
                "HUB / MWBE / DBE certification pass-through for minority-set-aside line items",
                "Dedicated RFP response desk — every bid, every deadline, professionally submitted",
                "Retail buyer meetings with H-E-B, Kroger, and independent grocery regional buyers",
                "Tradeshow &amp; sponsorship activation — booth logistics, pallet delivery, branded coolers",
                "Airport concession introductions via Houston Airport System master concessionaires",
                "Fleet + last-mile logistics using our existing Houston distribution footprint",
                "Marketing amplification through AskIsaac AI platform + sterilemate.com domain assets",
                "Chain-of-custody, lot traceability, audit-ready delivery documentation for government purchasers",
                "Monthly reporting scorecard — pipeline, orders won, orders shipped, commission earned",
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 rounded-xl bg-slate-50 border border-slate-200 p-4 hover:border-emerald-400/60 transition-colors">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-slate-700 leading-6" dangerouslySetInnerHTML={{ __html: item }} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section className="relative py-24 px-6 lg:px-10 bg-gradient-to-br from-blue-950 via-slate-900 to-cyan-950 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img
            src={img("lw_proposal_hero.jpg")}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-blue-950 via-slate-950/90 to-transparent" />

        <div className="relative max-w-5xl mx-auto text-center">
          <p className="text-xs font-bold tracking-[0.3em] text-amber-400 uppercase">Ready when you are</p>
          <h2 className="mt-3 text-4xl md:text-6xl font-black leading-tight">
            Let&apos;s pour Living Waters<br/>
            <span className="text-amber-400">everywhere hydration matters.</span>
          </h2>
          <p className="mt-6 text-lg md:text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
            The full proposal is one click away. Download the PDF, review the terms, and let&apos;s schedule a working session. Sterile Mate LLC is prepared to move to execution within 30 days.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <a
              href={pdfUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="lw-cta-download-pdf"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-lg shadow-2xl shadow-amber-500/30 transition-all hover:-translate-y-0.5"
            >
              <Download className="w-5 h-5" />
              Download the Proposal (PDF)
            </a>
            <Link
              to={proposalUrl}
              data-testid="lw-cta-view-proposal"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-lg bg-white/10 border border-white/30 hover:bg-white/15 text-white font-semibold text-lg transition-all backdrop-blur"
            >
              View Full Proposal Online
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          <div className="mt-16 pt-8 border-t border-white/10 text-sm text-blue-200 flex flex-wrap justify-center items-center gap-x-6 gap-y-2">
            <span><b className="text-white">George Broughton III</b> — President, Sterile Mate LLC DBA Bleu Flux</span>
            <span>·</span>
            <a href="tel:8009883892" className="hover:text-white">800.988.3892</a>
            <span>·</span>
            <a href="mailto:george.broughton@sterilemate.com" className="hover:text-white">george.broughton@sterilemate.com</a>
            <span>·</span>
            <span>Houston, TX</span>
          </div>
        </div>
      </section>

      {/* footer strip */}
      <footer className="bg-slate-950 border-t border-white/5 py-6 px-6 text-center text-xs text-slate-500">
        © 2026 Sterile Mate LLC DBA Bleu Flux · Confidential Proposal SM-BF-LW-2026-001 ·
        <Link to="/proposals" className="ml-2 text-cyan-400 hover:text-cyan-300">All Proposals</Link>
      </footer>
    </div>
  );
}
